<template>
  <footer class="bg-primary-600">
    <div
      class="lg:w-full max-w-7xl px-4 mx-auto py-6 md:py-16 pt-14 md:pt-16 text-white"
    >
      <ProgramLists />

      <Divider
        class="before:border-t-[0.5px] before:border-surface-500 !my-10"
      />

      <UniversityLists />

      <Divider
        class="before:border-t-[0.5px] before:border-surface-500 !mt-10"
      />
    </div>
    <div
      class="lg:w-full max-w-7xl px-2 lg:px-4 mx-auto py-6 md:py-16 pt-4 md:pt-4 text-white"
    >
      <div class="grid grid-cols-1 md:grid-cols-4 gap-20 px-5 lg:px-0">
        <!-- Left Column: Logo + Description -->
        <div class="md:col-span-1">
          <img src="~/assets/img/logo-white.svg" class="max-w-[200px]" />
          <p class="text-sm py-4 leading-6 tracking-wide">
            UniLinks is an education platform that helps students explore
            universities in Asia, compare programs, and receive experience-based
            consultations enriched with real insights from students who have
            studied there.
          </p>
        </div>

        <!-- Right Columns: 2x3 Grid -->
        <div class="md:col-span-3">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-x-12 gap-y-8">
            <!-- Row 1 -->
            <div>
              <p class="text-lg font-bold mb-4">Home</p>
              <ul class="space-y-2 text-sm">
                <li>
                  <NuxtLink to="/free-consult" class="hover:underline"
                    >General Consultation</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/universities" class="hover:underline"
                    >All Universities</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/explore" class="hover:underline"
                    >All Programs</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/scholarships" class="hover:underline"
                    >Scholarship</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/ambassador" class="hover:underline"
                    >Ambassadors</NuxtLink
                  >
                </li>
              </ul>
            </div>

            <div>
              <p class="text-lg font-bold mb-4">Address</p>
              <a
                href="https://www.google.com/maps?q=Unit+1603,+16th+Floor,+The+L.+Plaza,+367–375+Queen’s+Road+Central,+Sheung+Wan,+Hong+Kong"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm leading-4 hover:underline block"
              >
                Unit 1603, 16th Floor<br />
                The L. Plaza, 367–375 Queen’s Road Central<br />
                Sheung Wan, Hong Kong
              </a>
            </div>

            <div>
              <p class="text-lg font-bold mb-4">Email</p>
              <a
                href="mailto:info@unilinks.co"
                class="text-sm hover:underline flex items-center gap-x-2"
                ><i class="pi pi-envelope text-lg"></i>info@unilinks.co</a
              >

              <!-- Social Icons -->
              <div class="flex gap-x-4 items-center text-xl mt-4">
                <a href="https://facebook.com/unilinks.myanmar" target="_blank"
                  ><i class="pi pi-facebook"></i
                ></a>
                <a href="https://instagram.com/unilinks_myanmar" target="_blank"
                  ><i class="pi pi-instagram"></i
                ></a>
                <a href="https://youtube.com/@UniLinksCompany" target="_blank"
                  ><i class="pi pi-youtube"></i
                ></a>
                <a
                  href="https://linkedin.com/company/unilinks-global"
                  target="_blank"
                  ><i class="pi pi-linkedin"></i
                ></a>
              </div>
            </div>

            <!-- Row 2 -->
            <div>
              <p class="text-lg font-bold mb-4">Resources</p>
              <ul class="space-y-2 text-sm">
                <li>
                  <NuxtLink to="/webinars" class="hover:underline"
                    >Webinars</NuxtLink
                  >
                </li>
                <li>
                  <NuxtLink to="/blogs" class="hover:underline">Blogs</NuxtLink>
                </li>
              </ul>
            </div>

            <div>
              <p class="text-lg font-bold mb-4">Contact Numbers</p>

              <p class="text-sm mb-2.5">
                <a
                  href="tel:+66638493090"
                  class="flex items-center gap-2.5 hover:underline"
                  ><i class="pi pi-phone text-xl"></i> Thailand (Bangkok): +66
                  638493090</a
                >
              </p>

              <p class="text-sm">
                <a
                  href="tel:+959898330519"
                  class="flex items-center gap-2.5 hover:underline"
                  ><i class="pi pi-phone text-xl"></i>Myanmar: +95 9898330519</a
                >
              </p>
            </div>

            <div>
              <p class="text-lg font-bold mb-4">Chat & Messaging</p>
              <div class="flex items-center gap-x-4 text-xl">
                <a
                  href="https://wa.me/66638493090"
                  target="_blank"
                  class="flex items-center gap-2 hover:underline"
                >
                  <i class="pi pi-whatsapp"></i>
                </a>
                <a
                  href="https://t.me/unilinksmyanmar"
                  target="_blank"
                  class="flex items-center gap-2 hover:underline"
                >
                  <i class="pi pi-send"></i>
                </a>
                <a
                  href="viber://chat?number=%2B66638493090"
                  class="flex items-center gap-2 hover:underline"
                >
                  <i class="pi pi-comment"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex justify-between flex-wrap items-center px-5 lg:px-0 text-sm text-primary-200 mt-8 md:mt-20"
      >
        <p>© {{ displayYear }} Unilinks. All rights reserved.</p>
        <div class="flex items-center space-x-3 text-primary-200 mt-2 md:mt-0">
          <NuxtLink
            to="/terms-conditions"
            class="hover:text-white transition-colors duration-200"
          >
            Terms & Conditions
          </NuxtLink>
          <span>|</span>
          <NuxtLink
            to="/privacypolicy"
            class="hover:text-white transition-colors duration-200"
          >
            Privacy Policy
          </NuxtLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import LogoLight from "~/assets/img/logo-white.svg";
const startYear = 2024;
const currentYear = new Date().getFullYear();
const displayYear =
  startYear === currentYear ? `${startYear}` : `${startYear}–${currentYear}`;
</script>
